import type { BoardConfiguration } from "~/types/board_configuration";
import { sanitizeFontFamilyName } from "~/utils/string_helpers";

const BASE_LINK = "https://fonts.googleapis.com/css2?";
const STYLES = ":ital,wght@0,400;0,500;0,700;1,400;1,500;1,700";
const STANDARD_FONTS = [
  "Arial",
  "Courier New",
  "Garamond",
  "Georgia",
  "Helvetica",
  "Palatino",
  "Times",
  "Times New Roman",
  "Untitled Sans",
  "Verdana",
  "untitled_sans",
  "arial",
  "helvetica",
  "times_new_roman",
  "verdana",
];

function buildGoogleFontsLink(fonts: (string | null | undefined)[]) {
  const googleFontsLinks = fonts
    .filter((font) => !!font)
    .filter((font) => !STANDARD_FONTS.includes(font as string))
    .map((font) => `family=${font?.replace(/ /g, "+")}${STYLES}`)
    .join("&");

  if (googleFontsLinks.length === 0) {
    return null;
  }

  return `${BASE_LINK}${googleFontsLinks}`;
}

type CustomFontStyles = [string, string[]];

export function buildCustomFontStyles(config: BoardConfiguration | null): CustomFontStyles {
  let styles = ``;
  const links = [];
  if (config?.custom_primary_font_url) {
    links.push(config?.custom_primary_font_url);
    styles += `
      @font-face {
        font-family: ${sanitizeFontFamilyName(config?.primary_font)};
        src: url(${config?.custom_primary_font_url});
        font-weight: normal;
        font-style: normal;
      }
    `;
  }
  if (config?.custom_secondary_font_url) {
    links.push(config?.custom_secondary_font_url);
    styles += `
      @font-face {
        font-family: ${sanitizeFontFamilyName(config?.secondary_font)};
        src: url(${config?.custom_secondary_font_url});
        font-weight: normal;
        font-style: normal;
      }
    `;
  }
  if (config?.custom_primary_font_url_fallback) {
    links.push(config?.custom_primary_font_url_fallback);
    styles += `
      @font-face {
        font-family: ${sanitizeFontFamilyName(config?.primary_font_fallback)};
        src: url(${config?.custom_primary_font_url_fallback});
        font-weight: normal;
        font-style: normal;
      }
    `;
  }
  if (config?.custom_secondary_font_url_fallback) {
    links.push(config?.custom_secondary_font_url_fallback);
    styles += `
      @font-face {
        font-family: ${sanitizeFontFamilyName(config?.secondary_font_fallback)};
        src: url(${config?.custom_secondary_font_url_fallback});
        font-weight: normal;
        font-style: normal;
      }
    `;
  }
  return [styles, links];
}

export function buildGoogleFontsLinks(config: BoardConfiguration | null, preview = false) {
  const links: (string | null)[] = [];

  const googleFonts = [];
  !config?.custom_primary_font_url && googleFonts.push(config?.primary_font);
  !config?.custom_secondary_font_url && googleFonts.push(config?.secondary_font);
  googleFonts.push(config?.primary_font_fallback);
  googleFonts.push(config?.secondary_font_fallback);

  // If we're previewing, we want to load the fonts separately to avoid flashes of unstyled content as fonts are changed in Design Studio
  if (preview) {
    googleFonts.forEach((font_name) => {
      links.push(buildGoogleFontsLink([font_name]));
    });
  } else {
    links.push(buildGoogleFontsLink(googleFonts));
  }

  return links.filter((link) => !!link);
}
